import {
  ExtrasContent,
  ContentMap,
  ContentRootTree,
  Content,
  ContentType,
  emptyId,
  ContentTree,
} from './extras.domain';
import {
  GetProfileQuery,
  ContentIds as GraphQLContentIds,
  GetContentQuery,
} from 'generated/graphql';
import { CreateParams } from './extras.model';
import { ArrayItem, TMap } from '@client/new/helpers/types';
import { ProfileData } from '@design-system/components/mobile-preview/types';
import { toChildrenMap } from './tree';

export type GQLContent = GetContentQuery['content'];
export type GQLContentItems = GQLContent['contentItems'];

export const toExtrasContent = (
  c: GetContentQuery['content'],
): ExtrasContent => {
  const contentIds: ContentRootTree = toContentIds(c.contentIds);
  return {
    contentIds,
    contentMap: toContentMap(c.contentItems),
    childrenMap: toChildrenMap(contentIds),
  };
};

const toContentIds = (
  c: GraphQLContentIds | undefined | null,
): ContentRootTree => {
  if (!c?.id) return { id: 'empty' };

  const children = (c.items || [])
    .map(toContentIds)
    .filter((item): item is ContentRootTree => !!item);

  return {
    id: c.id,
    ...(children.length > 0 ? { items: children } : {}),
  };
};

const toContent = (v: ArrayItem<GQLContentItems>): Content => {
  const {
    type,
    value: { __typename, ...value },
  } = v;
  if ((value as any)['coverURL'] === null) (value as any)['coverURL'] = '';
  if ((value as any)['coverImage'] === null) (value as any)['coverImage'] = '';
  return { type, value } as Content;
};

const toContentMap = (items: GQLContentItems) =>
  items.reduce(
    (acc, item) => ({ ...acc, [item.value.id]: toContent(item) }),
    {} as ContentMap,
  );

export const rootAllowedTypes: Array<ContentType> = ['ContentTab'];

export const contentHierarchyRules: Partial<TMap<ContentType, ContentType[]>> =
  {
    ['ContentTab']: [
      'ContentLegacyGuide',
      'ContentLink',
      'ContentSlides',
      'ContentPDF',
    ],
    ['ContentSlides']: ['ContentImage'],
  };

export const recalculateContentTree = (
  p: Omit<CreateParams, 'data'> & { type: ContentType; id: string },
): ContentRootTree => {
  const isRoot = p.tree.id === emptyId;
  const isDirectChildOfRoot = p.parentId === p.tree.id;

  const validateAppend = (parentId: string): void => {
    const parent = p.contentMap[parentId];
    if (!parent) {
      throw new Error(`Parent with id ${parentId} not found in contentMap`);
    }

    const allowedChildren = contentHierarchyRules[parent.type];
    if (!allowedChildren || !allowedChildren.includes(p.type)) {
      throw new Error(
        `Content type ${p.type} cannot be appended to parent type ${parent.type}`,
      );
    }
  };

  const findParentInTree = (nodes: ContentTree[] = []): boolean => {
    for (const node of nodes) {
      if (node.id === p.parentId) return true;
      if (findParentInTree(node.items)) return true;
    }
    return false;
  };

  if (isRoot) {
    if (!rootAllowedTypes.includes(p.type)) {
      throw new Error(
        `Root can only have ${rootAllowedTypes.join(', ')} types`,
      );
    }
    return { id: p.id, items: [] };
  }

  if (isDirectChildOfRoot && p.parentId) {
    validateAppend(p.parentId);
    return cleanTree(
      {
        ...p.tree,
        items: [...(p.tree.items ?? []), { id: p.id }],
      },
      p.contentMap,
    );
  }

  if (p.parentId && !findParentInTree(p.tree.items)) {
    throw new Error(`Parent with id ${p.parentId} not found in tree`);
  }

  const addToTree = (node: ContentTree): ContentTree => {
    if (p.parentId && node.id === p.parentId) {
      validateAppend(p.parentId);
      return {
        ...node,
        items: [...(node.items ?? []), { id: p.id }],
      };
    }
    return {
      ...node,
      items: (node.items ?? []).map(addToTree),
    };
  };

  return cleanTree(
    {
      ...p.tree,
      items: (p.tree.items ?? []).map(addToTree),
    },
    p.contentMap,
  );
};

function cleanTree(
  tree: ContentRootTree,
  contentMap: ContentMap,
): ContentRootTree {
  const cleanNode = (node: ContentTree): ContentTree => {
    const type = contentMap[node.id]?.type;
    const allowedChildren = type && contentHierarchyRules[type];

    if (!allowedChildren || allowedChildren.length === 0) {
      return { id: node.id };
    }

    return {
      id: node.id,
      items: (node.items ?? []).map(cleanNode),
    };
  };

  return {
    id: tree.id,
    items: (tree.items ?? []).map(cleanNode),
  };
}

export const toProfileData = (p: GetProfileQuery['creator']): ProfileData => {
  if (!p) return {};
  return {
    firstName: p.firstName,
    lastName: p.lastName,
    title: p.title,
    profileInstagram: p.profileInstagram,
    profileYoutubeChannel: p.profileYoutubeChannel,
    profileTiktok: p.profileTiktok,
    mediaBanner: p.mediaBanner,
    mediaAvatar: p.mediaAvatar,
    customColorSchema: {
      primaryColor: p.customColorSchema?.primaryColor,
    },
  };
};
